import {
  forwardRef,
  startTransition,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  stripRedundantSectionHeadingFromBody,
  type ComplianceDocumentSectionRecord,
} from '@artem-lex/compliance-documents'
import type { Editor } from '@tiptap/core'
import { Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { replaceProxyUrlsWithPaths } from '@/shared/utils/compliance-document-images'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { DocumentMenuBar } from '@/components/document-menu-bar'
import { AddComplianceDocumentSectionDialog } from './AddComplianceDocumentSectionDialog'
import {
  ComplianceDocumentSectionBlock,
  type SectionEditorRegistration,
} from './ComplianceDocumentSectionBlock'
import { ComplianceDocumentLastSavedIndicator } from './ComplianceDocumentLastSavedIndicator'
import { RenameComplianceDocumentSectionSheet } from './RenameComplianceDocumentSectionSheet'
import { useComplianceDocumentManifestContext } from './ComplianceDocumentManifestContext'
import { useComplianceDocumentSectionsPersistence } from './useComplianceDocumentSectionsPersistence'
import type { ComplianceDocumentViewHandle } from './complianceDocumentViewHandle'
import {
  createCustomAppendixSection,
  readEditorMarkdown,
  reindexSections,
} from './complianceDocumentEditorUtils'
import type {
  ComplianceDocumentI18nNamespace,
  ComplianceDocumentTranslationKeyPrefix,
} from './complianceDocumentEditorTypes'
import { OutstandingCalloutNavigator } from './OutstandingCalloutNavigator'
import { useOutstandingCalloutNavigator } from './useOutstandingCalloutNavigator'

type ComplianceDocumentViewProps<T extends ComplianceDocumentSectionRecord> = {
  studioScopeId: string
  sections: T[]
  onSectionsChange: (sections: T[]) => void
  onAutosave: (sections: T[]) => Promise<void>
  onCreateVersion: (sections: T[]) => Promise<void>
  isAutosaving: boolean
  isCreatingVersion: boolean
  lastSavedAt: Date | null
  isGenerating: boolean
  onExportPdf: () => void | Promise<void>
  isExportPending: boolean
  generateMenu?: React.ReactNode
  onRegenerateSection?: (sectionId: string) => void
  contentSyncKey: number
  sectionDomIdPrefix: string
  translationKeyPrefix: ComplianceDocumentTranslationKeyPrefix
  i18nNamespace: ComplianceDocumentI18nNamespace
  inputIdPrefix: string
  imageUploadScopeId?: string
  shouldShowSectionEditor?: (section: T) => boolean
  streamingPanel?: React.ReactNode
  streamingSectionId?: string | null
  onFocusedEditorChange?: (editor: Editor | null, sectionId: string | null) => void
  bubbleMenuContent?: React.ReactNode
  bubbleMenuHidden?: boolean
  /** When true, editor blur does not schedule autosave (rewrite drawer is open). */
  rewritePanelOpen?: boolean
  /** When true, show prev/next navigation for outstanding placeholder callouts (RA only). */
  outstandingCalloutNavigatorEnabled?: boolean
}

export const ComplianceDocumentView = forwardRef(function ComplianceDocumentView<
  T extends ComplianceDocumentSectionRecord,
>(
  {
    studioScopeId,
    sections,
    onSectionsChange,
    onAutosave,
    onCreateVersion,
    isAutosaving,
    isCreatingVersion,
    lastSavedAt,
    isGenerating,
    onExportPdf,
    isExportPending,
    generateMenu,
    onRegenerateSection,
    contentSyncKey,
    sectionDomIdPrefix,
    translationKeyPrefix,
    i18nNamespace,
    inputIdPrefix,
    imageUploadScopeId,
    shouldShowSectionEditor,
    streamingPanel,
    streamingSectionId = null,
    onFocusedEditorChange,
    bubbleMenuContent,
    bubbleMenuHidden,
    rewritePanelOpen = false,
    outstandingCalloutNavigatorEnabled = false,
  }: ComplianceDocumentViewProps<T>,
  ref: React.ForwardedRef<ComplianceDocumentViewHandle>
) {
  const { t } = useTranslation(i18nNamespace)
  const { resolveSectionDisplayTitle } = useComplianceDocumentManifestContext()
  const editorRegistrationsRef = useRef<Map<string, SectionEditorRegistration>>(
    new Map()
  )
  const [toolbarSectionId, setToolbarSectionId] = useState<string | null>(null)
  const [isAddSectionDialogOpen, setIsAddSectionDialogOpen] = useState(false)
  const [renameSectionId, setRenameSectionId] = useState<string | null>(null)
  const [editorRegistrationVersion, setEditorRegistrationVersion] = useState(0)
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)

  const sortedSections = useMemo(
    () => [...sections].sort((left, right) => left.sortOrder - right.sortOrder),
    [sections]
  )

  const showSectionEditor = useCallback(
    (section: T) => {
      if (shouldShowSectionEditor != null) {
        return shouldShowSectionEditor(section)
      }
      return true
    },
    [shouldShowSectionEditor]
  )

  useEffect(() => {
    if (streamingSectionId == null || !isGenerating) {
      return
    }
    const sectionElement = document.getElementById(
      `${sectionDomIdPrefix}-section-${streamingSectionId}`
    )
    sectionElement?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [isGenerating, sectionDomIdPrefix, streamingSectionId])

  const registerSectionEditor = useCallback(
    (registration: SectionEditorRegistration) => {
      editorRegistrationsRef.current.set(registration.sectionId, registration)
      if (outstandingCalloutNavigatorEnabled) {
        setEditorRegistrationVersion((current) => current + 1)
      }
    },
    [outstandingCalloutNavigatorEnabled]
  )

  const unregisterSectionEditor = useCallback(
    (sectionId: string) => {
      editorRegistrationsRef.current.delete(sectionId)
      setToolbarSectionId((current) => (current === sectionId ? null : current))
      if (outstandingCalloutNavigatorEnabled) {
        setEditorRegistrationVersion((current) => current + 1)
      }
    },
    [outstandingCalloutNavigatorEnabled]
  )

  const handleSectionFocus = useCallback((sectionId: string) => {
    startTransition(() => {
      setToolbarSectionId(sectionId)
    })
  }, [])

  const handleSectionEditorReady = useCallback(
    (sectionId: string) => {
      startTransition(() => {
        setToolbarSectionId((current) => current ?? sectionId)
      })
      if (outstandingCalloutNavigatorEnabled) {
        setEditorRegistrationVersion((current) => current + 1)
      }
    },
    [outstandingCalloutNavigatorEnabled]
  )

  const handlePlaceholderMarksApplied = useCallback(() => {
    if (!outstandingCalloutNavigatorEnabled) {
      return
    }
    setEditorRegistrationVersion((current) => current + 1)
  }, [outstandingCalloutNavigatorEnabled])

  const toolbarEditor = (() => {
    if (toolbarSectionId == null) {
      return null
    }
    const editor =
      editorRegistrationsRef.current.get(toolbarSectionId)?.editorRef.current ??
      null
    if (editor == null || editor.isDestroyed) {
      return null
    }
    return editor
  })()

  useEffect(() => {
    onFocusedEditorChange?.(toolbarEditor, toolbarSectionId)
  }, [onFocusedEditorChange, toolbarEditor, toolbarSectionId])

  const flushAllSectionsToState = useCallback((): T[] => {
    return sections.map((section) => {
      const registration = editorRegistrationsRef.current.get(section.sectionId)
      const editor = registration?.editorRef.current
      if (editor == null || editor.isDestroyed) {
        return section
      }
      let markdown = stripRedundantSectionHeadingFromBody(
        readEditorMarkdown(editor),
        section.sectionId,
        resolveSectionDisplayTitle(section)
      )
      markdown = replaceProxyUrlsWithPaths(markdown)
      return { ...section, markdown }
    })
  }, [resolveSectionDisplayTitle, sections])

  const {
    scheduleAutosave,
    scheduleBlurAutosave,
    flushAndAutosaveSilent,
    flushSections,
    cancelAutosave,
    persistSectionsImmediately,
  } = useComplianceDocumentSectionsPersistence({
    baselineSections: sections,
    baselineKey: contentSyncKey,
    onSectionsChange,
    onAutosave,
    flushAllSectionsToState,
    reindexSections,
    isGenerating,
  })

  const isPersisting = isAutosaving || isCreatingVersion

  const handleCreateVersion = useCallback(async () => {
    cancelAutosave()
    const flushedSections = flushSections()
    onSectionsChange(flushedSections)
    await onCreateVersion(flushedSections)
  }, [cancelAutosave, flushSections, onCreateVersion, onSectionsChange])

  const handleExportPdfClick = useCallback(async () => {
    if (isExportPending || isPersisting || isGenerating) {
      return
    }
    await flushAndAutosaveSilent()
    await onExportPdf()
  }, [
    flushAndAutosaveSilent,
    isExportPending,
    isGenerating,
    isPersisting,
    onExportPdf,
  ])

  const handleSectionEditorBlur = useCallback(() => {
    if (rewritePanelOpen) {
      return
    }
    scheduleBlurAutosave()
  }, [rewritePanelOpen, scheduleBlurAutosave])

  useImperativeHandle(
    ref,
    () => ({
      flushAndAutosaveSilent,
      flushSectionsToState: flushSections,
      cancelAutosave,
    }),
    [cancelAutosave, flushAndAutosaveSilent, flushSections]
  )

  const handleAddSection = useCallback(
    (sectionTitle: string) => {
      const flushedSections = flushAllSectionsToState()
      const nextSection = createCustomAppendixSection(
        flushedSections,
        sectionTitle
      )
      const nextSections = reindexSections([...flushedSections, nextSection])
      onSectionsChange(nextSections)
      scheduleAutosave()
    },
    [flushAllSectionsToState, onSectionsChange, scheduleAutosave]
  )

  const handleRemoveCustomSection = (sectionId: string) => {
    const flushedSections = flushAllSectionsToState()
    editorRegistrationsRef.current.delete(sectionId)
    setToolbarSectionId((current) => (current === sectionId ? null : current))
    if (outstandingCalloutNavigatorEnabled) {
      setEditorRegistrationVersion((current) => current + 1)
    }
    const nextSections = reindexSections(
      flushedSections.filter((section) => section.sectionId !== sectionId)
    )
    onSectionsChange(nextSections)
    scheduleAutosave()
  }

  const renameTargetSection =
    renameSectionId == null
      ? null
      : (sortedSections.find(
          (section) => section.sectionId === renameSectionId
        ) ?? null)

  const renameTargetTitle =
    renameTargetSection == null
      ? ''
      : resolveSectionDisplayTitle(renameTargetSection)

  const handleRenameSection = useCallback(
    async (sectionId: string, nextTitle: string) => {
      const flushedSections = flushAllSectionsToState()
      const nextSections = flushedSections.map((section) => {
        if (section.sectionId !== sectionId) {
          return section
        }
        return { ...section, title: nextTitle }
      })
      try {
        await persistSectionsImmediately(nextSections)
      } catch {
        // Parent onAutosave already surfaces save failures via toast.
      }
    },
    [flushAllSectionsToState, persistSectionsImmediately]
  )

  const resolvedImageUploadScopeId = imageUploadScopeId ?? studioScopeId

  const {
    count: outstandingCalloutCount,
    handlePrevious: handleOutstandingCalloutPrevious,
    handleNext: handleOutstandingCalloutNext,
  } = useOutstandingCalloutNavigator({
    enabled: outstandingCalloutNavigatorEnabled,
    sections,
    editorRegistrationsRef,
    scrollContainerRef,
    shouldIncludeSection: showSectionEditor,
    editorRegistrationVersion,
    contentSyncKey,
  })

  return (
    <div className='flex min-h-0 flex-1 flex-col overflow-hidden'>
      <div className='shrink-0 pb-4'>
        <div className='flex w-full flex-wrap items-center justify-between gap-2'>
          <div className='flex items-center gap-2'>
            <Button
              type='button'
              size='sm'
              onClick={() => {
                void handleExportPdfClick()
              }}
              variant='outline'
              disabled={isExportPending || isPersisting || isGenerating}
            >
              {isExportPending
                ? t(`${translationKeyPrefix}.exporting`)
                : t(`${translationKeyPrefix}.exportPdf`)}
            </Button>
            {generateMenu}
            <Button
              type='button'
              size='sm'
              variant='outline'
              disabled={isGenerating}
              onClick={() => {
                setIsAddSectionDialogOpen(true)
              }}
            >
              <Plus className='me-2 size-4' />
              {t(`${translationKeyPrefix}.addAppendix`)}
            </Button>
          </div>

          <div className='flex flex-wrap items-center justify-end gap-3'>
            {outstandingCalloutNavigatorEnabled ? (
              <OutstandingCalloutNavigator
                count={outstandingCalloutCount}
                isGenerating={isGenerating}
                translationKeyPrefix={translationKeyPrefix}
                i18nNamespace={i18nNamespace}
                onPrevious={handleOutstandingCalloutPrevious}
                onNext={handleOutstandingCalloutNext}
              />
            ) : null}
            <Button
              type='button'
              onClick={() => {
                void handleCreateVersion()
              }}
              disabled={isPersisting || isGenerating}
            >
              {isCreatingVersion
                ? t(`${translationKeyPrefix}.savingVersion`)
                : t(`${translationKeyPrefix}.saveVersion`)}
            </Button>
          </div>
        </div>

        <div className='flex justify-end'>
          <ComplianceDocumentLastSavedIndicator
            isAutosaving={isAutosaving}
            lastSavedAt={lastSavedAt}
            translationKeyPrefix={translationKeyPrefix}
            i18nNamespace={i18nNamespace}
          />
        </div>

        <AddComplianceDocumentSectionDialog
          isOpen={isAddSectionDialogOpen}
          onClose={() => {
            setIsAddSectionDialogOpen(false)
          }}
          onConfirm={handleAddSection}
          translationKeyPrefix={translationKeyPrefix}
          i18nNamespace={i18nNamespace}
          inputIdPrefix={inputIdPrefix}
        />

        <RenameComplianceDocumentSectionSheet
          isOpen={renameTargetSection != null}
          onClose={() => {
            setRenameSectionId(null)
          }}
          onConfirm={(nextTitle) => {
            if (renameTargetSection == null) {
              return
            }
            void handleRenameSection(renameTargetSection.sectionId, nextTitle)
          }}
          initialTitle={renameTargetTitle}
          translationKeyPrefix={translationKeyPrefix}
          i18nNamespace={i18nNamespace}
          inputIdPrefix={inputIdPrefix}
        />

        {toolbarEditor != null ? (
          <DocumentMenuBar
            editor={toolbarEditor}
            imageUpload={
              resolvedImageUploadScopeId.length > 0
                ? { complianceId: resolvedImageUploadScopeId }
                : undefined
            }
          />
        ) : null}
      </div>

      {streamingPanel}

      <Card className='min-h-0 flex-1 overflow-hidden'>
        <div
          ref={scrollContainerRef}
          className='h-full scrollbar-gutter-stable overflow-y-auto overscroll-contain'
        >
          <div>
            {sortedSections.map((section) => {
              const sectionTitle = resolveSectionDisplayTitle(section)
              const isGeneratingThisSection =
                isGenerating && streamingSectionId === section.sectionId
              const showEditor = showSectionEditor(section)

              return (
                <ComplianceDocumentSectionBlock
                  key={section.sectionId}
                  section={section}
                  sectionTitle={sectionTitle}
                  sectionDomIdPrefix={sectionDomIdPrefix}
                  translationKeyPrefix={translationKeyPrefix}
                  i18nNamespace={i18nNamespace}
                  imageUploadScopeId={resolvedImageUploadScopeId}
                  contentSyncKey={contentSyncKey}
                  isGenerating={isGenerating}
                  isExportPending={isExportPending}
                  isGeneratingThisSection={isGeneratingThisSection}
                  onRegister={registerSectionEditor}
                  onUnregister={unregisterSectionEditor}
                  onRemoveCustomSection={handleRemoveCustomSection}
                  onRenameRequest={setRenameSectionId}
                  onRegenerateSection={onRegenerateSection}
                  onSectionFocus={handleSectionFocus}
                  onSectionEditorReady={handleSectionEditorReady}
                  onPlaceholderMarksApplied={handlePlaceholderMarksApplied}
                  onContentChange={scheduleAutosave}
                  onEditorBlur={handleSectionEditorBlur}
                  showEditor={showEditor}
                  bubbleMenuContent={bubbleMenuContent}
                  bubbleMenuHidden={bubbleMenuHidden}
                />
              )
            })}
          </div>
        </div>
      </Card>
    </div>
  )
}) as <T extends ComplianceDocumentSectionRecord>(
  props: ComplianceDocumentViewProps<T> & {
    ref?: React.ForwardedRef<ComplianceDocumentViewHandle>
  }
) => React.ReactElement
